import { useMutation, useQueryClient } from "@tanstack/react-query"
import { RedeemCodeDocument, type RedeemCodeFailureReason, type RedeemCodeMutation } from "~/graphql/generated/graphql"
import { useGraphQLClient } from "~/shared/api/GraphQLClientContext"
import { toEntitlementItem, withSeatOrdinals } from "../projections"
import type { EntitlementItem } from "../types"

/**
 * Three outcomes, not two. `failure` is the server saying "that code is no
 * good", which arrives as a 200 and is a normal answer; `error` is the request
 * not completing at all. Collapsing them would render an expired MLC token as
 * "we don't recognize that code", which is the wrong thing to tell someone and
 * the wrong place to send them to debug it.
 */
export type RedeemOutcome =
  | { readonly kind: "success"; readonly code: string; readonly items: readonly EntitlementItem[] }
  | {
      readonly kind: "failure"
      readonly reason: RedeemCodeFailureReason
      /** The server's own text. Logged, not rendered — see `redeemMessages.ts`. */
      readonly detail: string | undefined
      /** ALREADY_REDEEMED carries what the account already owns; every other reason carries nothing. */
      readonly items: readonly EntitlementItem[]
    }
  | { readonly kind: "error" }

export interface RedeemCodeResult {
  /** `onSuccess` fires only for a granted code — not for a rejected one, which is a 200. */
  readonly redeem: (code: string, onSuccess?: () => void) => void
  readonly isPending: boolean
  readonly outcome: RedeemOutcome | undefined
  readonly reset: () => void
}

/**
 * MLC's codes are uppercase alphanumeric and its own field uppercases as you
 * type. Doing it here rather than in the input keeps the caret out of it, and a
 * pasted code carrying a space or a lowercase letter is a typo the customer
 * cannot see — not a code that should be rejected.
 */
export const normalizeCode = (code: string): string => code.trim().toUpperCase()

const toOutcome = (result: RedeemCodeMutation["redeemCode"]): RedeemOutcome => {
  if (result.__typename === "RedeemCodeSuccess") {
    return {
      kind: "success",
      code: result.code,
      /**
       * Same projection the sections use, ordinals included. The confirmation
       * grid sits directly above them, and a redemption that seats a product
       * twice would otherwise show two identical cards here and two labelled
       * ones below.
       */
      items: withSeatOrdinals((result.grantedItems ?? []).map(toEntitlementItem)),
    }
  }

  return {
    kind: "failure",
    reason: result.reason,
    detail: result.message ?? undefined,
    items: withSeatOrdinals((result.existingItems ?? []).map(toEntitlementItem)),
  }
}

/**
 * POC only, and gated the same way `useEntitlements` is: `redeemCode` lives on
 * the local facade and not on the deployed graph.
 *
 * Unlike the list, this one is a WRITE against a real UAT account through
 * whichever token minted `MLC_ACCESS_TOKEN` — a redeemed code is consumed and
 * cannot be handed back. See POC.md's disclosure: the seats land on the token
 * holder's account, not on whoever is signed in to the app.
 */
export const useRedeemCode = (): RedeemCodeResult => {
  const client = useGraphQLClient()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (code: string) => client.request(RedeemCodeDocument, { code: normalizeCode(code) }),
    /**
     * No retry. A redemption is not idempotent from the customer's side — a
     * retried request that the first attempt actually applied comes back
     * ALREADY_REDEEMED, which reads as a failure for a code that worked.
     */
    retry: false,
    onSuccess: data => {
      if (data.redeemCode.__typename !== "RedeemCodeSuccess") {
        return
      }
      /**
       * The seats are real now, so the sections below are stale. `libraryV2` is
       * deliberately NOT invalidated: it is a projection written asynchronously,
       * so a refetch this soon returns the same rows and just spends a request
       * to redraw them. The confirmation grid is what makes the new items
       * visible immediately, which is why `grantedItems` comes from the commerce
       * response rather than from a re-read.
       */
      void queryClient.invalidateQueries({ queryKey: ["entitlements"] })
    },
  })

  return {
    redeem: (code: string, onSuccess?: () => void) => {
      mutation.mutate(code, {
        onSuccess: data => {
          if (data.redeemCode.__typename === "RedeemCodeSuccess") {
            onSuccess?.()
          }
        },
      })
    },
    isPending: mutation.isPending,
    outcome: mutation.isError ? { kind: "error" } : mutation.data ? toOutcome(mutation.data.redeemCode) : undefined,
    reset: () => {
      mutation.reset()
    },
  }
}
